import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable } from 'react-use-observable';

import { CloseCircleOutlined as CancelIcon, QuestionCircleOutlined as HelpOutlineIcon } from '@ant-design/icons';
import type { RadioChangeEvent } from 'antd';
import { Alert, Radio, Tooltip } from 'antd';

import MaskedInput from 'components/MaskedInput';
import { tap } from 'rxjs/operators';
import Modal from 'v2/components/Shared/Modal';
import { sendEventToGA } from 'v2/helpers/analytics';
import { numberToBRL } from 'v2/helpers/money';
import type { RequestAnticipation } from 'v2/services/anticipation';
import { anticipationService } from 'v2/services/anticipation';
import * as Yup from 'yup';

import { type AnticipationByValue, type AnticipationOptions, type Props, ANTICIPATION_FRAUD_GROUP } from './constants';

function AnticipationModal({
  anticipationData,
  isOpen,
  setClose,
  updateTransferAvailable,
  handleUpdateAvailableAmount,
  handleOpenSuggestionAnticipation,
  loadSuggestionAnticipation,
  hasSuggestionAnticipation,
  anticipationGroupInfo
}: Props) {
  const history = useHistory();
  const [showSuccess, setShowSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingByValue, setIsLoadingByValue] = useState(false);

  const [valueToAnticipate, setValueToAnticipate] = useState(null);
  const [valueToAnticipateByValue, setValueToAnticipateByValue] = useState<null | AnticipationByValue>(null);
  const [anticipateOptionSelected, setAnticipateOptionSelected] = useState('');

  const [formIsValid, setFormIsValid] = useState(true);
  const [formMessageError, setFormMessageError] = useState(null);

  const [currentBalance] = useObservable(() => anticipationService.getCurrentBalance(), []);
  const [totalInvoices] = useObservable(() => anticipationService.getTotalInvoices(), []);

  useObservable(() => anticipationService.loadData(), []);

  const [requestAnticipation] = useCallbackObservable((anticipation: RequestAnticipation) => {
    setIsLoading(true);

    return anticipationService.requestAnticipation(anticipation).pipe(
      tap(
        () => {
          setIsLoading(false);
          setShowSuccess(true);
          updateTransferAvailable();
          handleUpdateAvailableAmount();
        },
        err => {
          toast.error(err);
          setIsLoading(false);
        }
      )
    );
  }, []);

  const [getAvailableByValue] = useCallbackObservable((requestedValue: number) => {
    setIsLoadingByValue(true);
    return anticipationService.getAvailableByValue(requestedValue).pipe(
      tap(({ data }) => setValueToAnticipateByValue(data)),
      tap(() => setIsLoadingByValue(false))
    );
  }, []);

  const nothingToAnticipate = !currentBalance && !showSuccess;
  const valueRequestItsTotal = currentBalance === valueToAnticipate;
  const hasAnticipationOptions = !!valueToAnticipateByValue;
  const somethingIsLoading = isLoading || isLoadingByValue;
  const anticipationOptionNotSelected = !valueRequestItsTotal && hasAnticipationOptions && !anticipateOptionSelected;
  const valueRequestedItsExact = !!valueToAnticipateByValue?.request_value;
  const requestedorTotalInvoices = valueToAnticipateByValue?.request_value?.invoices || totalInvoices;

  const balanceFormatted = typeof currentBalance === 'number' ? numberToBRL(currentBalance) : '-';
  const currentFixedFee = anticipationData?.anticipation_fees?.fixed_manual_fee
    ? `${anticipationData?.anticipation_fees?.fixed_manual_fee}%`
    : '2.99%';
  const currentDynamicFee = anticipationData?.anticipation_fees?.dynamic_manual_fee ?? 0.1;

  const validateValue = async (value: string) => {
    const anticipateSchema = Yup.object().shape({
      valueToAnticipate: Yup.number()
        .required()
        .min(0.01, `Valor mínimo: ${numberToBRL(0.01)}`)
        .max(currentBalance, `Valor máximo: ${balanceFormatted}`)
    });

    try {
      await anticipateSchema.validate({ valueToAnticipate: value });
      setFormIsValid(true);
      setFormMessageError(null);
    } catch (err) {
      setFormIsValid(false);
      setFormMessageError(err.message);
    }
  };

  const submitRequest = () => {
    const anticipationDataRequest = {
      amount_requested: currentBalance,
      total_invoices: totalInvoices
    };

    if (anticipateOptionSelected) {
      sendEventToGA({
        category: 'Novo Extrato',
        action: 'Antecipação_Parcial',
        label: `Sugestão_${anticipateOptionSelected === 'low_value' ? 'Menor' : 'Maior'}_Valor`
      });

      anticipationDataRequest.amount_requested = (
        valueToAnticipateByValue[
          anticipateOptionSelected as keyof typeof valueToAnticipateByValue
        ] as AnticipationOptions
      ).value;
      anticipationDataRequest.total_invoices = (
        valueToAnticipateByValue[
          anticipateOptionSelected as keyof typeof valueToAnticipateByValue
        ] as AnticipationOptions
      ).invoices;
    }

    if (valueRequestedItsExact) {
      anticipationDataRequest.amount_requested = valueToAnticipateByValue.request_value.value;
      anticipationDataRequest.total_invoices = valueToAnticipateByValue.request_value.invoices;
    }

    loadSuggestionAnticipation();
    requestAnticipation(anticipationDataRequest);
  };

  const handleOpenTransferModal = () => {
    history.push('/producer/financial-statement?a=transfer');
  };

  const handleGoToTranfer = () => {
    setIsLoading(true);
    setTimeout(() => {
      updateTransferAvailable();
      handleUpdateAvailableAmount();
      setIsLoading(false);
      handleOpenTransferModal();
      setClose();
    }, 3000);
  };

  const getCopyNextStepButton = () => {
    if (somethingIsLoading) return 'Carregando...';
    if (showSuccess) return 'Realizar transferência';
    if (!valueRequestItsTotal && !hasAnticipationOptions) return 'Verificar';
    return 'Confirmar antecipação';
  };

  const handleCloseSuccessAnticipation = () => {
    setClose();

    if (hasSuggestionAnticipation) {
      handleOpenSuggestionAnticipation();
    }
  };

  const getFunctionNextStepButton = () => {
    if (!valueRequestItsTotal && !hasAnticipationOptions) return handleGetAvailableByValue();
    if (!showSuccess) return submitRequest();
    return handleGoToTranfer();
  };

  const shouldDisableNextStepButton = () => {
    return (
      nothingToAnticipate ||
      !formIsValid ||
      (anticipationOptionNotSelected && !valueRequestedItsExact) ||
      somethingIsLoading
    );
  };

  const handleInputChange = (value: string) => {
    setValueToAnticipateByValue(null);
    setAnticipateOptionSelected('');
    validateValue(value);
    setValueToAnticipate(value);
  };

  const handleInputReset = () => {
    setValueToAnticipateByValue(null);
    setAnticipateOptionSelected('');
    setValueToAnticipate('');
    validateValue('0');
  };

  const handleChangeAnticipationOptions = (event: RadioChangeEvent) => setAnticipateOptionSelected(event.target.value);

  const handleGetAvailableByValue = () => {
    sendEventToGA({
      category: 'Novo Extrato',
      action: 'Antecipação_Parcial',
      label: 'Verificar'
    });

    getAvailableByValue(valueToAnticipate);
  };

  const getAnticipationOption = (props: AnticipationOptions, optionValue: string) => {
    if (!props) return null;

    return (
      <Radio value={optionValue} className='text-sm'>
        <div className='flex flex-col'>
          <strong className='text-base'>{numberToBRL(props.value)}</strong>
          <span>
            <strong>Tarifa:</strong> {numberToBRL(props.fee)}
          </span>
          <span>
            <strong>Faturas:</strong> {props.invoices}
          </span>
        </div>
      </Radio>
    );
  };

  useEffect(() => {
    if (currentBalance) {
      setValueToAnticipate(currentBalance);
    }
  }, [currentBalance]);

  return (
    <Modal
      open={isOpen}
      size='xs'
      setClose={setClose}
      textTitle={!showSuccess ? 'Antecipar saldo' : 'Parabéns!'}
      textReturnButton='Agora não'
      textProceedButton={getCopyNextStepButton()}
      handleProceedClick={getFunctionNextStepButton}
      handleReturnClick={!showSuccess ? setClose : handleCloseSuccessAnticipation}
      isProceedButtonDisabled={shouldDisableNextStepButton()}
    >
      {!showSuccess && (
        <>
          <span className='mb-2 block'>Saldo disponível para antecipação:</span>
          <span className='text-primary mb-2 block text-lg! font-semibold'>{balanceFormatted}</span>
          <span className='mb-2 block'>Digite o valor desejado:</span>
          <MaskedInput
            className='[&_.__message]:text-error mb-0! [&_.__message]:text-xs [&_.__message]:leading-[150%] [&_.__message]:font-bold [&_fieldset]:border-[#b0bec5] [&_input]:h-auto [&_input]:text-[22px]! [&>div]:pr-0'
            name='valueToAnticipate'
            placeHolder='R$ 0,00'
            mask='money'
            maskCleanValue
            disabled={nothingToAnticipate || somethingIsLoading}
            value={valueToAnticipate}
            onChange={handleInputChange}
            error={!formIsValid}
            endAdornment={<CancelIcon className='cursor-pointer text-base text-[#B0BEC5]' onClick={handleInputReset} />}
          />
          {!formIsValid && formMessageError && <div className='mt-1 text-xs text-red-500'>{formMessageError}</div>}

          {(valueRequestItsTotal || valueRequestedItsExact) && (
            <small className='text-grey-500 [&>a]:text-grey-500 mt-4 block text-xs leading-[150%] [&>a]:font-bold'>{`Esse valor é referente a ${requestedorTotalInvoices} fatura${
              requestedorTotalInvoices > 1 ? 's' : ''
            } antecipáve${requestedorTotalInvoices > 1 ? 'is' : 'l'}.`}</small>
          )}

          {!valueRequestItsTotal && !valueRequestedItsExact && valueToAnticipateByValue && (
            <>
              <div className='mt-4 flex items-center justify-between text-base leading-normal'>
                <span>Escolha o melhor valor para suas necessidades:</span>
                <Tooltip
                  title='Os valores sugeridos foram obtidos das faturas antecipáveis (da venda mais antiga para a mais nova).'
                  placement='top'
                >
                  <HelpOutlineIcon style={{ fontSize: 16 }} />
                </Tooltip>
              </div>

              <Radio.Group
                className='mt-4 w-full'
                value={anticipateOptionSelected}
                onChange={handleChangeAnticipationOptions}
              >
                <div className='flex justify-around'>
                  {getAnticipationOption(valueToAnticipateByValue?.low_value, 'low_value')}
                  {getAnticipationOption(valueToAnticipateByValue?.up_value, 'up_value')}
                </div>
              </Radio.Group>
            </>
          )}

          {anticipationGroupInfo?.id === ANTICIPATION_FRAUD_GROUP && (
            <Alert
              type='warning'
              className='mt-4! text-xs'
              message={
                <>
                  Sua antecipação foi limitada para {anticipationGroupInfo.percent}% do valor disponível para
                  antecipação! Em caso de dúvidas entre em contato com o nosso suporte.
                </>
              }
              showIcon
            />
          )}

          <div className='text-grey-500 [&>a]:text-grey-500 mt-4 block text-xs leading-[150%] [&>a]:font-bold'>
            <span>
              O recebimento antes do prazo incorre tarifa administrativa de {currentFixedFee} + {currentDynamicFee}% por
              dia antecipado.
            </span>
            <a
              href='https://ajuda.eduzz.com/hc/pt-br/articles/4402057357211-Como-funciona-a-antecipa%C3%A7%C3%A3o-%C3%93rbita-'
              rel='noopener noreferrer'
              target='_blank'
            >
              Saiba mais
            </a>
          </div>
        </>
      )}

      {showSuccess && (
        <div className='flex flex-col items-center text-center text-sm [&>img]:mb-6 [&>strong]:pt-4'>
          <img width={64} height={63} src='/img/success_icon.svg' alt='Ícone verde de sucesso' />
          <span>Parabéns, sua solitação de antecipação de valores foi efetuada com sucesso!</span>
          <strong>
            A lista de faturas antecipadas será enviada para o seu e-mail assim que a solicitação for processada.
          </strong>
          <strong>Em alguns instantes seu saldo disponível estará atualizado.</strong>
        </div>
      )}
    </Modal>
  );
}

export default AnticipationModal;
